const MARKER_ATTR = 'data-md-has'
const HAS_CLASS_RE = /:has\(\s*\.([A-Za-z_][\w-]*)\s*\)/g
const MARKUP_CLASS_RE = /class\s*=\s*["']([^"']+)["']/gi
const DECLARATION_RE = /([^{};@]+):([^;{}]+);/g

const compileCache = new Map<string, CompiledMetadataCss>()
const markerRefCounts = new Map<Element, Map<string, number>>()

export type CompiledMetadataCss = {
  css: string
  ancestorMarkers: string[]
}

export function collectMarkupClassNames(html: string): string[] {
  const names: string[] = []
  const re = new RegExp(MARKUP_CLASS_RE.source, 'gi')
  let match: RegExpExecArray | null
  while ((match = re.exec(String(html ?? ''))) !== null) {
    for (const part of String(match[1] ?? '').split(/\s+/)) {
      if (part) names.push(part)
    }
  }
  return names
}

/**
 * PrimeVue defaults beat bare metadata declarations. Add !important only to
 * real `prop: value` declarations. A split-on-semicolon rewrite used to emit
 * `} !important;`, which made the browser recover the whole sheet.
 */
export function ensureDeclarationImportant(css: string): string {
  return String(css ?? '').replace(DECLARATION_RE, (full, prop: string, value: string) => {
    if (value.includes('!important')) return full
    const property = prop.trim()
    if (!property) return full
    return `${prop}:${value.trim()} !important;`
  })
}

/**
 * `:has(.static-marker)` is true for the whole life of this stylesheet, because
 * the marker node is rendered with it. An attribute on ancestors keeps the rule
 * off other forms. A class would be wiped by Vue's class patch (element-renderer,
 * form-overlays-block), and the button rules would fall back to the theme fill.
 */
export function compileInjectedMetadataCss(
  cssText: string,
  beforeHtml = '',
  afterHtml = '',
): CompiledMetadataCss {
  const css = String(cssText ?? '')
  const markers = collectMarkupClassNames(`${beforeHtml ?? ''}\n${afterHtml ?? ''}`)
  const markerKey = [...new Set(markers)].sort().join(' ')
  const cacheKey = `${markerKey}\0${css}`
  const cached = compileCache.get(cacheKey)
  if (cached) return cached
  if (compileCache.size > 40) compileCache.clear()

  const markerSet = new Set(markers)
  const ancestorMarkers: string[] = []
  const rewritten =
    css && markerSet.size > 0
      ? css.replace(HAS_CLASS_RE, (full, className: string) => {
          if (!markerSet.has(className)) return full
          if (!ancestorMarkers.includes(className)) ancestorMarkers.push(className)
          return `[${MARKER_ATTR}~="${className}"]`
        })
      : css

  const compiled: CompiledMetadataCss = {
    css: rewritten ? ensureDeclarationImportant(rewritten) : '',
    ancestorMarkers,
  }
  compileCache.set(cacheKey, compiled)
  return compiled
}

function writeMarkerAttribute(el: Element, counts: Map<string, number>) {
  const tokens = [...counts.keys()].filter((name) => (counts.get(name) ?? 0) > 0)
  if (tokens.length === 0) {
    if (el.getAttribute(MARKER_ATTR) != null) el.removeAttribute(MARKER_ATTR)
    return
  }
  const next = tokens.join(' ')
  if (el.getAttribute(MARKER_ATTR) === next) return
  el.setAttribute(MARKER_ATTR, next)
}

/** Reference-counted so nested wrappers can share one ancestor marker. */
export function retainMetadataHasMarkers(start: Element, markers: readonly string[]): () => void {
  const unique = [...new Set(markers.map((name) => String(name ?? '').trim()).filter(Boolean))]
  if (!unique.length || typeof start.setAttribute !== 'function') return () => undefined

  const touched: Element[] = []
  let node: Element | null = start
  while (node) {
    touched.push(node)
    let counts = markerRefCounts.get(node)
    if (!counts) {
      counts = new Map()
      markerRefCounts.set(node, counts)
    }
    for (const marker of unique) {
      counts.set(marker, (counts.get(marker) ?? 0) + 1)
    }
    writeMarkerAttribute(node, counts)
    node = node.parentElement
  }

  let released = false
  return () => {
    if (released) return
    released = true
    for (const el of touched) {
      const counts = markerRefCounts.get(el)
      if (!counts) continue
      for (const marker of unique) {
        const next = (counts.get(marker) ?? 1) - 1
        if (next <= 0) counts.delete(marker)
        else counts.set(marker, next)
      }
      writeMarkerAttribute(el, counts)
      if (counts.size === 0) markerRefCounts.delete(el)
    }
  }
}
